import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Inbox, Link2 } from "lucide-react";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatDateTime } from "@/lib/dates";
import {
  creaInvitoRete, getInvitiRete, getRichiesteRete, isAccessoSospeso, toggleInvitoRete,
} from "@/api/rete";
import type { InvitoRete, RichiestaRete } from "@/types/rete";
import { AccessoSospeso } from "./AccessoSospeso";

// Lo stato tecnico della richiesta detto con le parole del mestiere.
const STATO: Record<string, string> = {
  dati_inseriti: "In verifica",
  pec_inviata: "Contratto inviato",
  contratto_accettato: "Contratto accettato",
  attiva: "Attiva",
  rifiutata: "Rifiutata",
  scaduta: "Scaduta",
};

const invitoLink = (token: string) => `${window.location.origin}/api/affiliazione/r/${token}`;

// Copia con ripiego (stessa funzione della console admin): navigator.clipboard può mancare.
async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

export function AffiliazioniRete() {
  const [inviti, setInviti] = useState<InvitoRete[] | null>(null);
  const [richieste, setRichieste] = useState<RichiestaRete[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [azioneErrore, setAzioneErrore] = useState<string | null>(null);
  const [sospeso, setSospeso] = useState(false);
  const [nome, setNome] = useState("");
  const [inVolo, setInVolo] = useState(false);
  const [copiato, setCopiato] = useState<number | null>(null);

  // Guardia d'ordine: una ricarica vecchia (partita prima) che risponde dopo una
  // più recente non deve sovrascriverne il risultato. `seq` numera le chiamate,
  // `mio` è il numero di questa chiamata: se non coincide più con `seq.current`
  // quando arriva la risposta, è superata e si scarta.
  const seq = useRef(0);
  const carica = useCallback(async () => {
    const mio = ++seq.current;
    setError(null);
    try {
      const [i, r] = await Promise.all([getInvitiRete(), getRichiesteRete()]);
      if (mio !== seq.current) return;
      setInviti(i);
      setRichieste(r);
    } catch (e) {
      if (mio !== seq.current) return;
      if (isAccessoSospeso(e)) setSospeso(true);
      else setError("Impossibile caricare le affiliazioni.");
    }
  }, []);

  useEffect(() => {
    void carica();
    return () => { seq.current++; };   // allo smontaggio ogni risposta in volo è vecchia
  }, [carica]);

  async function crea() {
    if (nome.trim().length < 2) return;
    setInVolo(true);
    setAzioneErrore(null);
    try {
      await creaInvitoRete(nome.trim());
      setNome("");
      await carica();
    } catch {
      setAzioneErrore("Creazione del link non riuscita.");
    } finally {
      setInVolo(false);
    }
  }

  async function toggle(i: InvitoRete) {
    setAzioneErrore(null);
    try {
      await toggleInvitoRete(i.id);
      await carica();
    } catch {
      setAzioneErrore("Aggiornamento non riuscito.");
    }
  }

  if (sospeso) return <AccessoSospeso />;
  if (error) return <ErrorState message={error} />;
  if (inviti === null || richieste === null) {
    return <p className="text-sm text-muted-foreground">Caricamento...</p>;
  }

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-text">Le tue affiliazioni</h2>

      <section className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="flex flex-col gap-3 px-4 py-3 border-b border-border sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h3 className="font-semibold text-text">Link di invito</h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Chi apre il link compila la richiesta; l'amministrazione la verifica e la attiva.
            </p>
          </div>
          <form
            className="flex flex-col gap-1.5 sm:flex-row sm:items-end sm:gap-2"
            onSubmit={(e) => { e.preventDefault(); crea(); }}
          >
            <label className="flex flex-col gap-1 text-sm text-text">
              Nome affiliato
              <Input className="w-full sm:w-56" placeholder="es. Bar Sport di Rossi"
                value={nome} onChange={(e) => setNome(e.target.value)} />
            </label>
            <Button type="submit" size="sm" disabled={inVolo || nome.trim().length < 2}>
              Crea link
            </Button>
          </form>
        </div>
        {azioneErrore && (
          <p className="px-4 pt-3 text-sm text-destructive" role="alert">{azioneErrore}</p>
        )}
        {inviti.length === 0 ? (
          <div className="p-4">
            <EmptyState
              icon={<Link2 size={26} className="text-brand" />}
              title="Nessun link di invito"
              description="Crea un link e consegnalo a chi vuoi affiliare."
            />
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {inviti.map((i) => (
              <li key={i.id}
                  className={`px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between ${i.attivo ? "" : "opacity-60"}`}>
                <div className="min-w-0">
                  <div className="font-medium text-text flex items-center gap-2">
                    {i.nome}
                    {!i.attivo && <Badge variant="outline" className="text-muted-foreground">Disattivato</Badge>}
                  </div>
                  <code className="text-xs text-muted-foreground break-all">{invitoLink(i.token)}</code>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button size="sm" variant="outline"
                    onClick={async () => {
                      const ok = await copyText(invitoLink(i.token));
                      setCopiato(ok ? i.id : null);
                      if (ok) setTimeout(() => setCopiato(null), 2500);
                    }}>
                    {copiato === i.id ? "Copiato" : "Copia link"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => toggle(i)}>
                    {i.attivo ? "Disattiva" : "Riattiva"}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="bg-card rounded-lg border border-border overflow-hidden">
        <h3 className="font-semibold text-text px-4 pt-4 pb-3">Richieste</h3>
        {richieste.length === 0 ? (
          <div className="px-4 pb-4">
            <EmptyState
              icon={<Inbox size={26} className="text-brand" />}
              title="Nessuna richiesta"
              description="Le richieste compaiono qui quando qualcuno compila un tuo link di invito."
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Attività</TableHead>
                  <TableHead>Referente</TableHead>
                  <TableHead className="hidden md:table-cell">Link</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="hidden md:table-cell">Inviata il</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {richieste.map((r) => (
                  <TableRow key={r.id} className="[&_td]:py-3 align-top">
                    <TableCell className="font-medium text-text">
                      {r.ragione_sociale}
                      <div className="text-xs text-muted-foreground font-normal mt-0.5">
                        {r.comune} ({r.provincia})
                      </div>
                    </TableCell>
                    <TableCell>
                      {r.nome_referente}
                      <div className="text-xs text-muted-foreground mt-0.5">{r.cellulare}</div>
                      {r.numero_verificato && (
                        <span className="inline-flex items-center gap-1 text-emerald-700 text-xs">
                          <Check className="size-3.5" /> verificato
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground">
                      {r.invito_nome ?? "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="whitespace-nowrap">{STATO[r.stato] ?? r.stato}</Badge>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground text-xs whitespace-nowrap">
                      {r.created_at ? formatDateTime(r.created_at) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>
    </div>
  );
}
